/**
 * 图片库选择器（modal）
 *
 * 列出当前用户已经生成完成的图片，支持单选或多选。
 * 选中的图片以 resultUrl 形式（统一媒体网关 /api/media/file/... URL）返回。
 */

import { useEffect, useRef, useState } from 'react';
import { X, Check, Loader2, ImageIcon } from 'lucide-react';
import { api } from '../../../services/api';
import { withAuthToken } from '../../../utils/storageUrl';

interface ImageItem {
  id: string;
  prompt: string;
  resultUrl: string | null;
  thumbnailUrl?: string | null;
  createdAt: string;
}

interface ImageLibraryPickerProps {
  isOpen: boolean;
  onClose: () => void;
  /** 单选返回单个 url，多选返回数组 */
  onSelect: (urls: string[]) => void;
  /** 最多可选数量；1 = 单选 */
  maxSelect?: number;
  /** 标题 */
  title?: string;
}

export function ImageLibraryPicker({
  isOpen,
  onClose,
  onSelect,
  maxSelect = 1,
  title,
}: ImageLibraryPickerProps) {
  const [items, setItems] = useState<ImageItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const loadedOnce = useRef(false);

  useEffect(() => {
    if (!isOpen || loadedOnce.current) return;
    loadedOnce.current = true;
    setLoading(true);
    setError('');
    api
      .getImageList()
      .then((res: any) => {
        const list: ImageItem[] = (res?.data || []).filter(
          (it: any) => it && it.status === 'completed' && it.resultUrl
        );
        setItems(list);
      })
      .catch((err: any) => {
        setError(err?.message || '加载失败');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [isOpen]);

  // 关闭时重置选择状态
  useEffect(() => {
    if (!isOpen) {
      setSelected([]);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isSingle = maxSelect === 1;

  const toggle = (url: string) => {
    if (isSingle) {
      onSelect([url]);
      onClose();
      return;
    }
    setSelected((prev) => {
      if (prev.includes(url)) return prev.filter((x) => x !== url);
      if (prev.length >= maxSelect) return prev;
      return [...prev, url];
    });
  };

  const handleConfirm = () => {
    onSelect(selected);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-light-border flex items-center justify-between">
          <h3 className="text-base font-semibold text-light-text">
            {title || '从我的图片中选择'}
            {!isSingle && (
              <span className="text-xs text-light-muted ml-2">
                {selected.length} / {maxSelect}
              </span>
            )}
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-light-hover">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-light-muted">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : error ? (
            <div className="text-center py-12 text-rose-500 text-sm">{error}</div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-light-muted">
              <ImageIcon className="w-12 h-12 mb-2 text-light-border" />
              <p className="text-sm">暂无可用图片</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
              {items.map((item) => {
                const url = item.resultUrl as string;
                const checked = selected.includes(url);
                const previewSrc = withAuthToken(item.thumbnailUrl || item.resultUrl);
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => toggle(url)}
                    className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-all ${
                      checked
                        ? 'border-accent-blue ring-2 ring-accent-blue/40'
                        : 'border-transparent hover:border-light-border'
                    }`}
                  >
                    <img
                      src={previewSrc}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    {checked && (
                      <div className="absolute top-1 right-1 w-6 h-6 rounded-full bg-accent-blue text-white flex items-center justify-center">
                        <Check className="w-4 h-4" />
                      </div>
                    )}
                    {!isSingle && checked && (
                      <div className="absolute bottom-1 left-1 w-5 h-5 rounded-full bg-accent-blue text-white text-xs flex items-center justify-center font-bold">
                        {selected.indexOf(url) + 1}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {!isSingle && (
          <div className="px-6 py-3 border-t border-light-border flex justify-end gap-2">
            <button onClick={onClose} className="btn-secondary text-sm">
              取消
            </button>
            <button
              onClick={handleConfirm}
              disabled={selected.length === 0}
              className="btn-primary text-sm disabled:opacity-50"
            >
              确定（{selected.length}）
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
